import api from './api';

export interface Transformation {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  hierarchy_type: 'TRANSFORMATION' | 'PROGRAM' | 'PROJECT' | 'SUB_PROJECT';
  parent_id?: string;
  is_template: boolean;
  template_id?: string;
  industry?: string;
  functional_area?: string;
  transformation_type?: string;
  complexity_level?: number;
  estimated_duration_months?: number;
  estimated_cost_range_min?: number;
  estimated_cost_range_max?: number;
  tags?: string[];
  objectives?: string[];
  expected_outcomes?: string[];
  key_activities?: string[];
  cost_drivers?: Record<string, any>;
  cost_calculation_formula?: string;
  custom_assumptions?: Record<string, any>;
  calculated_cost?: number;
  organizational_hierarchy_id?: string;
  organizational_hierarchy_name?: string;
  functional_hierarchy_id?: string;
  functional_hierarchy_name?: string;
  geographic_hierarchy_id?: string;
  geographic_hierarchy_name?: string;
  product_hierarchy_id?: string;
  manager_id?: string;
  marketplace_rating?: number;
  marketplace_reviews_count?: number;
  published_at?: string;
  conversion_status: 'NOT_CONVERTED' | 'CONVERTED' | 'PARTIALLY_CONVERTED';
  is_converted_to_project: boolean;
  converted_at?: string;
  status: 'PLANNING' | 'ACTIVE' | 'COMPLETED' | 'ON_HOLD' | 'CANCELLED';
  start_date?: string;
  end_date?: string;
  budget?: number;
  owner_id: string;
  created_at: string;
  updated_at: string;
  first_name?: string;
  last_name?: string;
  // Additional properties for UI compatibility
  vision?: string;
  comments?: string;
  owner_name?: string;
  scope?: string;
  timeline_start?: string;
  timeline_end?: string;
  classification?: string;
}

export interface CreateTransformationData {
  name: string;
  description: string;
  hierarchy_type: 'TRANSFORMATION' | 'PROGRAM' | 'PROJECT' | 'SUB_PROJECT';
  parent_id?: string;
  is_template?: boolean;
  template_id?: string;
  industry?: string;
  functional_area?: string;
  transformation_type?: string;
  complexity_level?: number;
  estimated_duration_months?: number;
  estimated_cost_range_min?: number;
  estimated_cost_range_max?: number;
  tags?: string[];
  objectives?: string[];
  expected_outcomes?: string[];
  key_activities?: string[];
  cost_drivers?: Record<string, any>;
  cost_calculation_formula?: string;
  custom_assumptions?: Record<string, any>;
  organizational_hierarchy_id?: string;
  functional_hierarchy_id?: string;
  geographic_hierarchy_id?: string;
  manager_id?: string;
  conversion_status?: 'NOT_CONVERTED' | 'CONVERTED' | 'PARTIALLY_CONVERTED';
  is_converted_to_project?: boolean;
  status?: 'PLANNING' | 'ACTIVE' | 'COMPLETED' | 'ON_HOLD' | 'CANCELLED';
  start_date?: string;
  end_date?: string;
  budget?: number;
  // Additional properties for UI compatibility
  vision?: string;
  comments?: string;
  owner_name?: string;
  scope?: string;
  timeline_start?: string;
  timeline_end?: string;
  classification?: string;
}

export interface UpdateTransformationData extends Partial<CreateTransformationData> {}

export const transformationsService = {
  // Get all transformations
  async getTransformations(): Promise<Transformation[]> {
    const response = await api.get('/generic/transformations');
    return response.data.data || response.data;
  },

  // Get a single transformation
  async getTransformation(id: string): Promise<Transformation> {
    const response = await api.get(`/transformations/${id}`);
    return response.data.data || response.data;
  },

  // Create a new transformation
  async createTransformation(data: CreateTransformationData): Promise<Transformation> {
    const response = await api.post('/generic/transformations', data);
    return response.data.data || response.data;
  },

  // Update a transformation (use transformation API so classification and other fields are handled correctly)
  async updateTransformation(id: string, data: UpdateTransformationData): Promise<Transformation> {
    const response = await api.put(`/transformations/${id}`, data);
    return response.data.data || response.data;
  },

  // Delete a transformation
  async deleteTransformation(id: string): Promise<void> {
    await api.delete(`/generic/transformations/${id}`);
  },

  // Phase 2: Transformation members
  async getMembers(transformationId: string): Promise<TransformationMember[]> {
    const response = await api.get(`/transformations/${transformationId}/members`);
    return response.data?.data ?? response.data ?? [];
  },
  async addMember(transformationId: string, userId: string, role?: string | null): Promise<void> {
    await api.post(`/transformations/${transformationId}/members`, { user_id: userId, role: role ?? undefined });
  },
  async removeMember(transformationId: string, userId: string): Promise<void> {
    await api.delete(`/transformations/${transformationId}/members/${userId}`);
  },
};

export interface TransformationMember {
  id: string;
  user_id: string;
  role: string | null;
  created_at: string;
  email?: string;
  first_name?: string;
  last_name?: string;
} 